import React from 'react';

import Box from '@mui/material/Box';
import type { BoxProps } from '@mui/material/Box';
import type { GridRowModel } from '@mui/x-data-grid';
import { List } from 'immutable';

import { dateSelectors } from 'app-components/flux/date';
import type { tagsSelectors, tagsTypes } from 'app-components/flux/tags';
import {
  transactionsActions,
  transactionsSelectors,
  transactionsTypes,
  transactionsUtils,
} from 'app-components/flux/transactions';
import type { TransactionProps, TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { TransactionSubType } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';
import type RootState from 'app-components/utils/redux-store/rootState';
import { OverflowTooltip } from 'components/OverflowTooltip';
import { DateTime } from 'luxon';
import { splitItemsToArraySortedByPosition } from 'app-components/flux/transactions/splitsHelpers';
import type { AppDispatch } from 'store';
import { makeStyles } from 'tss-react/mui';
import trackEventTTI from 'utils/trackEventTTI';

export const TextCell = ({ value, sx, ...otherProps }: BoxProps & { value?: string }) => (
  <OverflowTooltip title={value}>
    <Box
      sx={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&:focus-visible': {
          outline: 'none',
        },
        ...sx,
      }}
      tabIndex={0}
      {...otherProps}
    >
      {value}
    </Box>
  </OverflowTooltip>
);

/* eslint-disable tss-unused-classes/unused-classes */
export const useSharedGridStyles = makeStyles()((theme) => ({
  icon: {
    padding: 0,
    height: 20,
    width: 20,
    color: theme.palette.icon.main,
  },
  invoiceIcon: {
    width: 20,
    height: 20,
    color: theme.palette.icon.main,
  },

  reviewedButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    opacity: 0,
  },
  hoverReviewed: {
    opacity: 1,
    backgroundColor: theme.palette.icon.light,
    color: theme.palette.background.paper,
    '&:hover, &:focus-within': {
      backgroundColor: theme.palette.amount.positive,
    },
  },
  checked: {
    opacity: 1,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.success.main,
    '&.Mui-checked': {
      color: theme.palette.success.main,
    },
  },
  checkIcon: {
    height: 20,
    width: 20,
    borderRadius: 10,
  },
  unpaidText: {
    color: theme.palette.text.tertiary,
  },
  investmentTransactionTypeField: {
    marginRight: 0,
  },
  investmentTransactionTypeFieldInputRoot: {
    paddingRight: '20px !important',
  },
  amountCell: {
    alignContent: 'center',
  },
  hideRightSeparator: {
    '& > .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
  },
  editableTextField: {
    ...theme.typography.paragraphRegular,
    height: '100%',
  },
  investmentFullWidth: {
    minWidth: '100%',
  },
  center: {
    alignItems: 'center',
  },
}));

// remove once we remove old columns
export const updateInvestmentTransaction = (investmentTransactionObj: TransactionProps, dispatch: AppDispatch) => {
  const investmentTransactionNormalized = transactionsUtils.normalizeInvestmentTransaction(investmentTransactionObj);
  const payload = new transactionsTypes.InvestmentTransaction(investmentTransactionNormalized);
  trackEventTTI(tracker.events.txnSave, transactionsSelectors.getTrackingProps(undefined, payload));
  dispatch(
    transactionsActions.updateTransaction(payload, {
      undo: { userMessage: 'Transaction updated.' },
    }),
  );
};

export const formatInvestmentsUnits = (value: number | null | undefined) => {
  if (typeof value != 'number') {
    return '-';
  }
  const unitsNumber = Math.abs(value);
  if (!Number.isNaN(unitsNumber)) {
    return unitsNumber % 1 ? unitsNumber.toFixed?.(4) : String(unitsNumber);
    // TODO: add special handling for crypto 0.00000234
  }
  return '-';
};

export const formatInvestmentsPrice = (value: number | null | undefined) => {
  if (typeof value != 'number') {
    return '-';
  }
  const price = Math.abs(value);
  if (!Number.isNaN(price)) {
    return price.toFixed?.(2);
  }
  return '-';
};

export const normalizeInvestmentValue = (amount: number | string | undefined) => {
  if (amount === '-' || amount === undefined || amount === null) return null;
  return Number(amount);
};

export function computeDateFields(date: number | string, pending: boolean, createdAt?: string | null) {
  const dt =
    typeof date === 'number' ? DateTime.fromMillis(date, { zone: 'utc' }) : DateTime.fromISO(date, { zone: 'utc' });

  const dateMs = dt.toMillis();
  const dateLabel = dt.toLocaleString(DateTime.DATE_MED);

  const createdAtMs = createdAt ? DateTime.fromISO(createdAt).toMillis() : undefined;

  let monthKey;
  if (!pending) {
    const y = dt.year;
    const m = dt.month;
    monthKey = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}`; // "2025-01"
  } else {
    monthKey = 'Pending';
  }

  return { date: dt.toISODate(), dateMs, dateLabel, createdAtMs, monthKey };
}

export const getCoa = (txn: TransactionRecord) => {
  if (txn.type === 'INVOICE') {
    switch (txn.subtype) {
      case TransactionSubType.InvoiceCreditMemo:
        return 'Credit Memo';
      case TransactionSubType.SalesReceipt:
        return 'Sales Receipt';
      case TransactionSubType.SalesRefund:
        return 'Sales Refund';
      default:
        return 'Invoice';
    }
  }
  return '--- Split ---';
};

// Transaction Item
//   - Transaction (parent)
//   - Split Item (if the item is a split-item)
//   - Item (either transaction or split-item if split-item)

export function getTxnTagIds(txnItem: transactionsTypes.TransactionItem) {
  const { transaction, splitItem, item } = txnItem;
  const tagIdsSet = new Set();

  if (transaction.split?.items && !splitItem) {
    // this item is a parent-txn that has split-children
    transaction.split.items.forEach((splitChild) => {
      splitChild.tags?.forEach((tag) => {
        if (tag.id) {
          tagIdsSet.add(tag.id);
        }
      });
    });
  } else {
    // item could be a txn with no split or a split-child, doesn't matter
    item.tags?.forEach((tag) => {
      if (tag.id) {
        tagIdsSet.add(tag.id);
      }
    });
  }
  return Array.from(tagIdsSet);
}

export type RowUpdateContext = {
  state: RootState;
  newRow: GridRowModel;
  oldRow: GridRowModel;
  txn: TransactionRecord;
  txnItem: transactionsTypes.TransactionItem;
  fieldsToUpdate: Partial<TransactionProps>;
  splitFieldsToUpdate: Partial<transactionsTypes.SplitItemProps>;
  splitItemId?: string;
  nestedChanged: boolean;
};

export type RowUpdateInitParams = Pick<RowUpdateContext, 'state' | 'newRow' | 'oldRow' | 'txnItem'>;

export const createRowUpdateContext = ({ state, newRow, oldRow, txnItem }: RowUpdateInitParams): RowUpdateContext => ({
  state,
  newRow,
  oldRow,
  txn: txnItem.transaction,
  txnItem,
  fieldsToUpdate: {},
  splitFieldsToUpdate: {},
  splitItemId: txnItem.splitItem?.id ?? undefined,
  nestedChanged: false,
});

export const getSplitUsageLabel = (txn: TransactionRecord): string => {
  const items = splitItemsToArraySortedByPosition(txn.split?.items ?? []);
  const normalizeBizId = (businessId: string | null | undefined): string =>
    businessId && businessId !== 'PERSONAL' ? businessId : 'PERSONAL';

  const firstBizId = normalizeBizId(items[0]?.businessId);
  const isBizSplit = items.some((item) => normalizeBizId(item?.businessId) !== firstBizId);

  return isBizSplit ? 'SPLIT' : firstBizId;
};

export const applyDateUpdate = (context: RowUpdateContext) => {
  if (!('date' in context.newRow) || context.newRow.isSplitItem) return;
  if (context.newRow.date === context.oldRow.date) return;

  const newDate = DateTime.fromMillis(context.newRow.date, { zone: 'utc' }).toISODate();
  if (!newDate) return;

  const currentDate = dateSelectors.getCurrentDateString(context.state);
  if (currentDate < newDate) {
    // txn moves to the upcoming section so delete row from the register
    context.newRow = { ...context.newRow, _action: 'delete' };
  }
  context.fieldsToUpdate.postedOn = newDate || context.txnItem.transaction.postedOn;
};

// TODO: Update
export const applyPayeeUpdate = (context: RowUpdateContext) => {
  if (!('payee' in context.newRow)) return;

  const { security } = context.newRow;
  const payeeName = context.newRow.payee || security?.symbol;
  if (!payeeName || payeeName === context.txn.payee) return;
  context.newRow = {
    ...context.newRow,
    ...(security && {
      category: transactionsUtils.investmentTransactionDescription(
        context.txn.merge({ symbol: security.symbol, payee: payeeName }),
        security.securityName,
      ),
    }),
    payee: context.newRow.payee,
  };
  context.fieldsToUpdate.payee = payeeName;
};

export const applyAmountUpdate = (context: RowUpdateContext) => {
  if (!('amount' in context.newRow) || context.newRow.split) return;
  if (context.newRow.amount === context.oldRow.amount) return;

  context.fieldsToUpdate.amount = context.newRow.amount || 0;
};

export const applyNotesUpdate = (context: RowUpdateContext) => {
  if (!('notes' in context.newRow)) return;
  if (context.newRow.notes === context.oldRow.notes) return;

  if (context.txnItem.splitItem) {
    context.splitFieldsToUpdate.memo = context.newRow.notes;
  } else {
    context.fieldsToUpdate.memo = context.newRow.notes;
  }
};

export const applyCheckUpdate = (context: RowUpdateContext) => {
  if (!('check' in context.newRow) || context.newRow.isSplitItem) return;
  if (context.newRow.check === context.oldRow.check) return;

  context.txn = context.txn.setIn(['check', 'number'], context.newRow.check);
  context.nestedChanged = true;
};

export const applyReviewedUpdate = (context: RowUpdateContext) => {
  if (!('reviewed' in context.newRow) || context.newRow.isSplitItem) return;
  if (context.newRow.reviewed === context.oldRow.reviewed) return;

  context.fieldsToUpdate.isReviewed = context.newRow.reviewed;
};

export const applyFlagUpdate = (context: RowUpdateContext) => {
  if (!('flag' in context.newRow)) return;
  if (context.newRow.flag === context.oldRow.flag) return;

  context.fieldsToUpdate.userFlag = context.newRow.flag;
};

export const applyAccountUpdate = (context: RowUpdateContext) => {
  if (!('accountId' in context.newRow) || context.newRow.isSplitItem) return;
  if (context.newRow.accountId === context.oldRow.accountId) return;

  context.fieldsToUpdate.accountId = context.newRow.accountId;
};

export const applyTagsUpdate = (context: RowUpdateContext, tagsById: ReturnType<typeof tagsSelectors.getTags>) => {
  if (!('tags' in context.newRow) || (context.newRow.split && !context.newRow.isSplitItem)) return;

  // newRow.tags and oldRow.tags are now arrays of tag IDs (strings)
  const newTagIds = Array.isArray(context.newRow.tags) ? context.newRow.tags : [];
  const oldTagIds = Array.isArray(context.oldRow.tags) ? context.oldRow.tags : [];

  // Sort both arrays to enable comparison
  const sortedNewTagIds = [...newTagIds].sort();
  const sortedOldTagIds = [...oldTagIds].sort();

  // Check if tags actually changed
  const tagsChanged =
    sortedNewTagIds.length !== sortedOldTagIds.length ||
    sortedNewTagIds.some((tagId, idx) => tagId !== sortedOldTagIds[idx]);

  if (!tagsChanged) return;

  // convert array of tag IDs to immutable List of Tag records
  const tagRecords = sortedNewTagIds
    .map((tagId) => tagsById.get(tagId))
    .filter((tag): tag is tagsTypes.Tag => tag !== undefined);
  const tags = List(tagRecords);

  if (context.splitItemId) {
    context.splitFieldsToUpdate.tags = tags;
  } else {
    context.fieldsToUpdate.tags = tags;
  }
};

export const applyCoaAndBusinessFields = (context: RowUpdateContext) => {
  if (context.newRow.split && !context.newRow.isSplitItem) return;

  const target = context.splitItemId ? context.splitFieldsToUpdate : context.fieldsToUpdate;

  if (context.newRow.coa.type !== context.oldRow.coa.type || context.newRow.coa.id !== context.oldRow.coa.id) {
    target.coa = context.newRow.coa;
  }

  if ('usage' in context.newRow && context.newRow.usage !== context.oldRow.usage) {
    target.businessId = context.newRow.usage;
  }

  if ('customerId' in context.newRow && context.newRow.customerId !== context.oldRow.customerId) {
    target.customerId = context.newRow.customerId || '';
  }

  if ('projectId' in context.newRow && context.newRow.projectId !== context.oldRow.projectId) {
    target.projectId = context.newRow.projectId || '';
  }
};

export const applyInvestmentUpdate = (
  context: RowUpdateContext,
  {
    isInvestmentsRegister,
  }: {
    isInvestmentsRegister: boolean;
  },
) => {
  if (!isInvestmentsRegister) return;

  // let investmentChanged = false;

  if (
    context.newRow.fee !== undefined &&
    normalizeInvestmentValue(context.newRow.fee) !== normalizeInvestmentValue(context.oldRow.fee)
  ) {
    context.fieldsToUpdate.commission = Number(context.newRow.fee) || 0;
    // investmentChanged = true;
  }

  if (
    context.newRow.quantity !== undefined &&
    normalizeInvestmentValue(context.newRow.quantity) !== normalizeInvestmentValue(context.oldRow.quantity)
  ) {
    context.fieldsToUpdate.units = Number(context.newRow.quantity) || 0;
    // investmentChanged = true;
  }

  if (
    context.newRow.price !== undefined &&
    normalizeInvestmentValue(context.newRow.price) !== normalizeInvestmentValue(context.oldRow.price)
  ) {
    const price = Number(context.newRow.price) || 0;
    const units = transactionsUtils.calcUnits(context.txn, price);
    if (units !== context.txn.units) {
      context.fieldsToUpdate.units = units;
      // investmentChanged = true;
    }
  }

  if (context.newRow.action !== undefined && context.newRow.action !== context.oldRow.action) {
    if (context.newRow.action === 'VIRTUAL_CASH_FLOW') {
      if (context.txn.type !== 'CASH_FLOW') {
        context.fieldsToUpdate.type = 'CASH_FLOW';
        const merged = context.txn.merge(context.fieldsToUpdate);
        const cashflowRecord = transactionsTypes.mkCashFlowTransaction(merged.toJS() as TransactionProps);
        context.txn = cashflowRecord;
        context.fieldsToUpdate = {};
        context.nestedChanged = true;
        return;
      }
    } else {
      context.fieldsToUpdate.type = 'INVESTMENT';
      context.fieldsToUpdate.investmentTxnType = context.newRow.action;
      // investmentChanged = true;
    }
  }

  if (context.newRow?.security && context.fieldsToUpdate.payee) {
    context.fieldsToUpdate.symbol = context.newRow.security.symbol || '';
    context.fieldsToUpdate.localSecurityId = context.newRow.security?.id || '';
    context.fieldsToUpdate.securityIdType = context.newRow.security.securityIdType || '';
    context.fieldsToUpdate.securityId = context.newRow.security.securityId || '';
    context.fieldsToUpdate.securityType = context.newRow.security.securityType || 'STOCK';
    // investmentChanged = true;
  }

  // if (investmentChanged) {
  //   context.nestedTxn = context.nestedTxn.merge(context.fieldsToUpdate);
  //   context.fieldsToUpdate = {};
  //   context.nestedChanged = true;
  //   return;
  // }

  return;
};
